// packages/web-ui/src/hooks/useKeymap.ts
//
// W2-S10 Task 3 (AC #3) — global keymap hook that opens the master keyboard
// cheat-sheet on `?` (bare) or `Ctrl+/`, suppresses the trigger when an
// editable element is focused, and exposes the open/close state to consumers.
//
// Polish-followups §8 — extends this module with `useGlobalKeymap()` covering
// the five wiring-§8 audit additions:
//
//   - `Ctrl+E`           → dispatch `tessa:exportActiveView` (view-renderer
//                          SVG export from focused / mounted `<ViewRenderer>`).
//   - `Ctrl+Shift+J`     → dispatch `tessa:toggleDebugTab` (utility-drawer
//                          Debug-tab visibility toggle / power-user override).
//                          NB: `<UtilityDrawer>` ALSO owns a direct keydown
//                          listener for this chord — that path remains the
//                          canonical action; the event channel exists so
//                          menus / palette items can trigger the same flow.
//   - `1`/`2`/`3`        → dispatch `tessa:cycleDrawerTab` with `{ index }`
//                          (Activity / Detail / Debug). ONLY when focus is in
//                          the utility-drawer subtree and not inside an
//                          editable element — guarded via the drawer-focus
//                          check below.
//   - `F6`               → dispatch `tessa:cyclePanelFocus`. Listener iterates
//                          the AppShell's four panel-root testids in cycle
//                          order and `.focus()`es the next one.
//   - `Ctrl+N`           → dispatch `tessa:newSession` (sessions sidebar
//                          `+ New` CTA action).
//
// The hook is independent of `<AppShell>` — any panel/component can import
// `useKeymap()` to subscribe to the cheat-sheet state. The owning component
// (`<KeyboardCheatSheet>`) drives the modal render based on `isOpen`.
//
// Tests (./useKeymap.test.tsx) cover:
//   - opens on bare `?`
//   - opens on `Ctrl+/`
//   - suppressed when an INPUT/TEXTAREA/contenteditable is focused
//   - `Esc` closes the overlay
//   - `close()` returns focus to previously-focused element
//   - polish §8 bindings — Ctrl+E / Ctrl+Shift+J / 1-2-3 / F6 / Ctrl+N

import { useCallback, useEffect, useRef, useState } from 'react';
import { isCheatSheetTrigger, isEditableElementFocused } from '../state/keymap.js';

/**
 * MED-14 — Custom DOM event that opens the keyboard cheat-sheet. Dispatched
 * from `<HelpPopover>`'s "Keyboard shortcuts" menu item; listened for by
 * `useKeymap()`. Window-level so any component can call
 * `window.dispatchEvent(new CustomEvent(CHEAT_SHEET_OPEN_EVENT))` without
 * having to thread a callback through props.
 */
export const CHEAT_SHEET_OPEN_EVENT = 'tessa:openCheatSheet';

/**
 * Polish §8 — custom-event channel names for the five global bindings.
 * Each action-target component (ExportSvg, UtilityDrawer, SessionList, …)
 * registers a `window` listener for the matching channel; `useGlobalKeymap`
 * is the sole dispatcher.
 *
 * Surface a constant per channel so callers and tests share the exact
 * string — typos in inline `new CustomEvent('tessa:…')` calls are a
 * notorious silent-fail vector.
 */
export const EXPORT_ACTIVE_VIEW_EVENT = 'tessa:exportActiveView';
export const TOGGLE_DEBUG_TAB_EVENT = 'tessa:toggleDebugTab';
export const CYCLE_DRAWER_TAB_EVENT = 'tessa:cycleDrawerTab';
export const CYCLE_PANEL_FOCUS_EVENT = 'tessa:cyclePanelFocus';
export const NEW_SESSION_EVENT = 'tessa:newSession';

/**
 * The four AppShell panel-root selectors used by the F6 panel-cycle handler.
 * Order matches UX §8.5: ActivityBar → ContextPanel → TabContent → Drawer.
 * Selectors are stable `data-testid` values already exercised by
 * `AppShell.test.tsx` — no markup change is required to support F6.
 */
const F6_PANEL_TESTIDS: ReadonlyArray<string> = [
  'activity-bar',
  'context-panel',
  'tab-content-area',
  'utility-drawer',
];

/** Detail payload for `tessa:cycleDrawerTab` — `index` is 0/1/2 (Activity/Detail/Debug). */
export interface CycleDrawerTabDetail {
  index: 0 | 1 | 2;
}

export interface UseKeymapOptions {
  /**
   * Document override — defaults to `document`. Tests pass a fresh document
   * to avoid leaking listeners across cases.
   */
  documentImpl?: Document;
  /**
   * When provided, the hook calls this on `Esc` instead of (or in addition to)
   * its own `close()`. Useful when the parent owns dismissal animation.
   */
  onEscape?: () => void;
}

export interface UseKeymapReturn {
  /** True when the cheat-sheet should be displayed. */
  isOpen: boolean;
  /** Programmatic open. */
  open: () => void;
  /** Programmatic close. Returns focus to the previously-focused element. */
  close: () => void;
  /** Toggle helper — useful for menu CTAs. */
  toggle: () => void;
}

export function useKeymap(options: UseKeymapOptions = {}): UseKeymapReturn {
  const [isOpen, setIsOpen] = useState(false);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  const open = useCallback((): void => {
    const doc =
      options.documentImpl ?? (typeof document !== 'undefined' ? document : undefined);
    if (doc !== undefined && doc.activeElement instanceof HTMLElement) {
      previouslyFocusedRef.current = doc.activeElement;
    }
    setIsOpen(true);
  }, [options.documentImpl]);

  const close = useCallback((): void => {
    setIsOpen(false);
    // Return focus to whatever was focused when the overlay opened (a11y).
    const prev = previouslyFocusedRef.current;
    if (prev !== null && typeof prev.focus === 'function') {
      // Defer one tick so React unmounts the overlay first.
      queueMicrotask(() => {
        prev.focus();
      });
    }
    previouslyFocusedRef.current = null;
  }, []);

  const toggle = useCallback((): void => {
    if (isOpen) close();
    else open();
  }, [isOpen, open, close]);

  useEffect(() => {
    const doc =
      options.documentImpl ?? (typeof document !== 'undefined' ? document : undefined);
    if (doc === undefined) return undefined;

    function handleKeyDown(event: KeyboardEvent): void {
      // Esc → close the overlay (only when it's open).
      if (event.key === 'Escape') {
        if (isOpen) {
          event.preventDefault();
          event.stopPropagation();
          if (options.onEscape !== undefined) options.onEscape();
          close();
        }
        return;
      }

      if (!isCheatSheetTrigger(event)) return;
      // AC #3 — suppress when an editable element is focused (so `?` typed
      // inside the chat composer doesn't open the cheat-sheet).
      if (isEditableElementFocused(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      // Toggle: if open, close; otherwise open. Toggle works for both
      // `?` (re-press dismisses) and `Ctrl+/` (chord toggle).
      if (isOpen) {
        close();
      } else {
        open();
      }
    }

    // MED-14 — `<HelpPopover>`'s "Keyboard shortcuts" menu item dispatches the
    // `tessa:openCheatSheet` custom event on click. We listen here so the
    // menu CTA opens the overlay without prop-drilling a callback through the
    // AppShell composition.
    function handleOpenEvent(): void {
      if (!isOpen) open();
    }

    doc.addEventListener('keydown', handleKeyDown);
    if (typeof window !== 'undefined') {
      window.addEventListener(CHEAT_SHEET_OPEN_EVENT, handleOpenEvent);
    }
    return (): void => {
      doc.removeEventListener('keydown', handleKeyDown);
      if (typeof window !== 'undefined') {
        window.removeEventListener(CHEAT_SHEET_OPEN_EVENT, handleOpenEvent);
      }
    };
  }, [isOpen, open, close, options]);

  return { isOpen, open, close, toggle };
}

// ─── Polish §8 — useGlobalKeymap ────────────────────────────────────────────

/** Returns true when the focused/event target is inside the utility drawer. */
function isFocusInUtilityDrawer(event: KeyboardEvent, doc: Document): boolean {
  // Prefer `event.target` (the element the chord was typed against);
  // fall back to `document.activeElement` if the event target wasn't an
  // element (jsdom edge cases / synthetic events from tests).
  const target =
    event.target instanceof Element ? event.target : doc.activeElement;
  if (!(target instanceof Element)) return false;
  // The drawer aside carries `data-testid="utility-drawer"` (UtilityDrawer.tsx
  // line 238). `.closest()` walks up the DOM, so this returns true for any
  // descendant — tab strip buttons, body panes, etc.
  return target.closest('[data-testid="utility-drawer"]') !== null;
}

/**
 * Cycle focus across the four AppShell panel roots. Reads the current
 * activeElement, finds its containing panel, then `.focus()`es the next
 * panel root in `F6_PANEL_TESTIDS` order. When none of the panels currently
 * own focus (or the activeElement is `<body>`), the cycle starts at the
 * ActivityBar so a fresh F6 press always lands somewhere visible.
 *
 * Each panel root already carries `tabIndex={-1}` (TabContentArea.tsx) or
 * is naturally focusable via its `<aside>`/`<nav>` semantics; for the ones
 * that don't have a tabindex (ActivityBar / ContextPanel / UtilityDrawer's
 * default render), we still call `.focus()` — modern browsers focus the
 * element when `tabIndex >= -1` is set on hover; here we set it
 * defensively before focusing.
 */
function cyclePanelFocus(doc: Document): void {
  const panels = F6_PANEL_TESTIDS.map((tid) =>
    doc.querySelector<HTMLElement>(`[data-testid="${tid}"]`),
  );
  // Drop panels not in the DOM (some modes hide them).
  const present = panels.filter((p): p is HTMLElement => p !== null);
  if (present.length === 0) return;

  // Find the index of the panel currently containing focus (if any).
  const active = doc.activeElement;
  let currentIdx = -1;
  if (active instanceof Element) {
    for (let i = 0; i < present.length; i += 1) {
      const panel = present[i];
      if (panel === undefined) continue;
      if (panel === active || panel.contains(active)) {
        currentIdx = i;
        break;
      }
    }
  }

  const nextIdx = (currentIdx + 1) % present.length;
  const target = present[nextIdx];
  if (target === undefined) return;

  // Defensive tabindex assignment — the panel roots are intended to be
  // focusable but only `<TabContentArea>` declares it explicitly. We don't
  // overwrite an existing positive tabindex.
  if (!target.hasAttribute('tabindex')) {
    target.setAttribute('tabindex', '-1');
  }
  target.focus();
}

export interface UseGlobalKeymapOptions {
  /** Document override — defaults to `document`. Test seam. */
  documentImpl?: Document;
}

/**
 * Polish §8 — register the global keydown listener that dispatches custom
 * events for the five wiring-audit additions. Mount once at the AppShell
 * root (siblings of `useKeymap`, `useTabKeybindings`, …).
 *
 * The hook owns NO state — it is a pure event-bridge. Action targets
 * (ExportSvg, UtilityDrawer, SessionList, …) register their own listeners.
 */
export function useGlobalKeymap(options: UseGlobalKeymapOptions = {}): void {
  useEffect(() => {
    const doc =
      options.documentImpl ?? (typeof document !== 'undefined' ? document : undefined);
    if (doc === undefined || typeof window === 'undefined') return undefined;

    function handler(event: KeyboardEvent): void {
      const docRef = doc;
      if (docRef === undefined) return;
      if (event.repeat) return;

      const ctrlOrMeta = event.ctrlKey || event.metaKey;
      const editableFocused = isEditableElementFocused(event.target);

      // ─── Ctrl+E → export the active view as SVG ───────────────────────
      // Always allow even mid-typing: matches browser's "Save Page As"
      // muscle memory. Editable-focus guard would block the common case
      // where the user has Ctrl+E re-bound to "end of line" — but our
      // listener intentionally captures Ctrl+E ONLY when a view-renderer
      // panel is mounted (ExportSvg's listener simply no-ops otherwise),
      // so the conflict surface is small. Still, suppress when editable
      // to match the Ctrl+E "go to end of line" muscle memory in inputs.
      if (
        ctrlOrMeta &&
        !event.shiftKey &&
        !event.altKey &&
        (event.key === 'e' || event.key === 'E')
      ) {
        if (editableFocused) return;
        event.preventDefault();
        event.stopPropagation();
        window.dispatchEvent(new CustomEvent(EXPORT_ACTIVE_VIEW_EVENT));
        return;
      }

      // ─── Ctrl+Shift+J → toggle Debug-tab override ─────────────────────
      // NB: `<UtilityDrawer>` ALREADY owns a direct listener for this
      // chord. The event-channel dispatch is purely for menu / palette
      // call-sites that want to programmatically trigger the same flow.
      // We DO NOT call `preventDefault()` here, so the direct UtilityDrawer
      // listener fires too (it owns the actual state mutation). The
      // listener fires for keydown bubble phase — same as ours — so the
      // event-channel dispatch is essentially a no-op when the drawer
      // owns focus (its listener also handles it). When the drawer is
      // closed or not yet mounted, only the event fires; that's harmless
      // (no listener will be registered).
      if (
        ctrlOrMeta &&
        event.shiftKey &&
        !event.altKey &&
        (event.key === 'J' || event.key === 'j')
      ) {
        if (editableFocused) return;
        // Don't preventDefault — UtilityDrawer's own listener owns the
        // canonical action and will handle preventDefault itself.
        window.dispatchEvent(new CustomEvent(TOGGLE_DEBUG_TAB_EVENT));
        return;
      }

      // ─── 1 / 2 / 3 → cycle drawer tab (only when focus in drawer) ────
      // The chord MUST NOT intercept when the user is typing — UX §6.2.6
      // composer mention/slash autocomplete listens for 1/2/3 to select
      // suggestion N. Triple-guard: no modifier, focus in drawer, focus
      // not in editable.
      if (
        !ctrlOrMeta &&
        !event.shiftKey &&
        !event.altKey &&
        (event.key === '1' || event.key === '2' || event.key === '3')
      ) {
        if (editableFocused) return;
        if (!isFocusInUtilityDrawer(event, docRef)) return;
        event.preventDefault();
        event.stopPropagation();
        const index = (event.key === '1' ? 0 : event.key === '2' ? 1 : 2) as 0 | 1 | 2;
        window.dispatchEvent(
          new CustomEvent<CycleDrawerTabDetail>(CYCLE_DRAWER_TAB_EVENT, {
            detail: { index },
          }),
        );
        return;
      }

      // ─── F6 → cycle focus between top-level panels ─────────────────────
      // F6 is a a11y standard (matches Firefox/Chrome chrome-panel cycle).
      // Fire even when an input is focused — the user explicitly wants to
      // navigate AWAY from the current panel. No `preventDefault` would
      // let the browser's own F6 also move focus into the address bar,
      // which is exactly the wrong behaviour; preventDefault to keep
      // focus inside the app.
      if (
        event.key === 'F6' &&
        !ctrlOrMeta &&
        !event.shiftKey &&
        !event.altKey
      ) {
        event.preventDefault();
        event.stopPropagation();
        // Dispatch the event for any external listeners that want to react
        // (announcer, tests), then perform the focus cycle ourselves.
        window.dispatchEvent(new CustomEvent(CYCLE_PANEL_FOCUS_EVENT));
        cyclePanelFocus(docRef);
        return;
      }

      // ─── Ctrl+N → new chat session ─────────────────────────────────────
      // Ctrl+N is also the browser's "new window" shortcut. Modern Chrome
      // hands it through when the app calls `preventDefault` from a
      // trusted keydown event listener; older Firefox versions may still
      // open a new window. We still register it because the cheat-sheet
      // documents it (UX §9.6). Suppress when editable focused so it
      // doesn't fire from a textarea (which the OS may eat anyway, but
      // be defensive).
      if (
        ctrlOrMeta &&
        !event.shiftKey &&
        !event.altKey &&
        (event.key === 'n' || event.key === 'N')
      ) {
        if (editableFocused) return;
        event.preventDefault();
        event.stopPropagation();
        window.dispatchEvent(new CustomEvent(NEW_SESSION_EVENT));
        return;
      }
    }

    doc.addEventListener('keydown', handler);
    return (): void => {
      doc.removeEventListener('keydown', handler);
    };
  }, [options]);
}
